import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { auth, firestore, functions, httpsCallable } from '@/firebase/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, onIdTokenChanged, User as FirebaseUser } from 'firebase/auth';
import { useRouter } from "next/navigation";
import { useRouter as usePagesRouter } from "next/router";
import * as Sentry from "@sentry/nextjs";
import { Subscription, UserPermissions, WorkspaceMembership } from "@/lib/types";
import { getUserPermissions } from "@/lib/UserPermissions";
import { useForceUpdate } from "@/hooks/useForceUpdate";
import { currentUser } from "@/firebase/firebaseAuth";
import { bootstrapAuthUser, clearAuthBootstrapProfileCache } from "@/lib/authBootstrapCoordinator";
import { resolveSelfSubscriptionRetrieveUid, shouldSkipBillingFetchForPath } from "@/lib/authBillingRoutes";
import { canProduceInActiveContext, nextWorkspaceListRetryDelayMs, readCachedWorkspaceMemberships, resolveActiveWorkspaceId, shouldClearStaleActiveWorkspace, shouldHideNativeHomeChrome, shouldOfferPersonalContext, writeCachedWorkspaceMemberships } from "@/lib/workspaceMembershipState";
import { getAuthHref, getSafeReturnTo } from "@/lib/AuthReturnTo";
import { CREDIT_RECONCILE_INTERVAL_MS, CREDIT_RECONCILE_TIMEOUT_MS, nextCreditReconcileStep } from "@/lib/creditReconciliation";
//

const SUBSCRIPTION_CACHE_PREFIX = 'braiv:lastKnownSubscription:';
const CREDITS_CACHE_PREFIX = 'braiv:lastKnownCredits:';
const ACTIVE_WORKSPACE_PREFIX = 'braiv:activeWorkspaceId:';
/** Legacy P1 sentinel; treat as Personal if still present in localStorage. */
const PERSONAL_CONTEXT_SENTINEL = '__personal__';

// Composite cache key so personal and per-workspace credits/subscription never
// collide in localStorage. Personal context uses the bare uid (back-compat).
function contextCacheId(uid: string, workspaceId: string | null): string {
  return workspaceId ? `${uid}:ws:${workspaceId}` : uid;
}
function getActiveWorkspaceStorageKey(uid: string): string {
  return `${ACTIVE_WORKSPACE_PREFIX}${uid}`;
}
function readPersistedActiveWorkspaceId(uid: string): string | null {
  if (typeof window === 'undefined' || !uid) return null;
  try {
    const raw = window.localStorage.getItem(getActiveWorkspaceStorageKey(uid));
    if (!raw || raw === PERSONAL_CONTEXT_SENTINEL) return null;
    return raw;
  } catch (error) {
    console.warn('Failed to read active workspace id:', error);
    return null;
  }
}
function writePersistedActiveWorkspaceId(uid: string, workspaceId: string | null) {
  if (typeof window === 'undefined' || !uid) return;
  try {
    if (workspaceId) {
      window.localStorage.setItem(getActiveWorkspaceStorageKey(uid), workspaceId);
    } else {
      window.localStorage.removeItem(getActiveWorkspaceStorageKey(uid));
    }
  } catch (error) {
    console.warn('Failed to persist active workspace id:', error);
  }
}
const USAGE_BILLING_UPDATED_KEY = 'usageBillingUpdatedAt';
const BILLING_CHANNEL_NAME = 'billing-updates';
// If Firebase Auth init stalls (e.g. residual IndexedDB contention from other
// Firebase services in a multi-window session), fail open after this delay so the
// UI never hangs on the loading shell. We fall back to auth.currentUser and never
// assume an authenticated state.
const AUTH_INIT_WATCHDOG_MS = 4000;
// Realtime credit invalidation: listens to the user's BillingAccount doc and
// refreshes the authoritative balance on change (covers async server-side debits
// that finish after the triggering callable returned). Disabled by default
// because the current Firestore rules do not grant clients read access to
// BillingAccounts - enable once a read rule (or a denormalized, user-readable
// balance doc) ships in braiv-firebase.
const REALTIME_CREDITS_ENABLED = process.env.NEXT_PUBLIC_ENABLE_REALTIME_CREDITS === 'true';
function getCreditsCacheKey(uid: string) {
  return `${CREDITS_CACHE_PREFIX}${uid}`;
}
function readCachedCredits(uid: string): number | null {
  if (typeof window === 'undefined' || !uid) return null;
  try {
    const cached = window.localStorage.getItem(getCreditsCacheKey(uid));
    if (cached === null) return null;
    const parsed = Number(cached);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to read cached credits:', error);
    return null;
  }
}
function writeCachedCredits(uid: string, credits: number) {
  if (typeof window === 'undefined' || !uid) return;
  try {
    window.localStorage.setItem(getCreditsCacheKey(uid), String(credits));
  } catch (error) {
    console.warn('Failed to cache credits:', error);
  }
}

// Notify other tabs/windows that the billing balance changed so they reconcile
// immediately rather than waiting for their next poll. Uses both a storage signal
// and BroadcastChannel; neither delivers to the originating tab (which already
// holds the authoritative value), so there is no refresh loop.
function broadcastBillingUpdated() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(USAGE_BILLING_UPDATED_KEY, String(Date.now()));
  } catch (error) {
    console.warn('Failed to write billing-updated storage signal:', error);
  }
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(BILLING_CHANNEL_NAME);
      channel.postMessage({
        type: 'billing-updated'
      });
      channel.close();
    }
  } catch (error) {
    console.warn('Failed to broadcast billing-updated:', error);
  }
}
export interface AuthContextInterface {
  user: FirebaseUser | null | undefined;
  permissions: UserPermissions | null | undefined;
  billingCredits: number | undefined | null;
  setBillingsCredits: (credits: number | undefined | null) => void;
  applyAuthoritativeCredits: (balance: number) => void;
  spendCreditsOptimistic: (cost: number) => void;
  reconcileCreditsAfterPurchase: (options?: {
    baseline?: number | null;
    timeoutMs?: number;
  }) => Promise<void>;
  refreshBillingCredits: (options?: {
    silent?: boolean;
    force?: boolean;
  }) => Promise<void>;
  refreshSubscription: (options?: {
    silent?: boolean;
  }) => Promise<void>;
  refreshBillingState: (options?: {
    force?: boolean;
    silent?: boolean;
  }) => Promise<void>;
  loading: boolean;
  forceUpdate: () => void;
  subscription: Subscription | null;
  creditsLoading: boolean;
  authDegraded: boolean;
  retryAuthInit: () => void;
  // Team workspaces (MVP). `activeWorkspaceId === null` means the personal
  // context (today's behavior). Credits/subscription above resolve from the
  // active context.
  workspaces: WorkspaceMembership[];
  activeWorkspaceId: string | null;
  activeWorkspace: WorkspaceMembership | null;
  setActiveWorkspaceId: (workspaceId: string | null) => void;
  refreshWorkspaces: () => Promise<WorkspaceMembership[]>;
  workspacesLoading: boolean;
  accountModel: string | null;
  homeWorkspaceId: string | null;
}
export const AuthContext = createContext<AuthContextInterface>({
  user: undefined,
  permissions: null,
  billingCredits: undefined,
  setBillingsCredits: () => {},
  applyAuthoritativeCredits: () => {},
  spendCreditsOptimistic: () => {},
  reconcileCreditsAfterPurchase: async () => {},
  refreshBillingCredits: async () => {},
  refreshSubscription: async () => {},
  refreshBillingState: async () => {},
  loading: true,
  forceUpdate: () => {},
  subscription: null,
  creditsLoading: true,
  authDegraded: false,
  retryAuthInit: () => {},
  workspaces: [],
  activeWorkspaceId: null,
  activeWorkspace: null,
  setActiveWorkspaceId: () => {},
  refreshWorkspaces: async () => {
    return [];
  },
  workspacesLoading: false,
  accountModel: null,
  homeWorkspaceId: null
});
export const useAuthContext = () => useContext(AuthContext);
function getSubscriptionCacheKey(uid: string) {
  return `${SUBSCRIPTION_CACHE_PREFIX}${uid}`;
}
function normalizeSubscriptionState(subscription: Subscription): Subscription {
  const isPaidOrTrial = Boolean(subscription.id && ["active", "trialing"].includes(subscription.status || ""));
  const hasStripeCustomer = Boolean(subscription.hasStripeCustomer ?? subscription.userStripeId);
  const hasPaymentMethod = Boolean(subscription.hasPaymentMethod ?? isPaidOrTrial);
  return {
    ...subscription,
    hasStripeCustomer,
    hasPaymentMethod,
    billingRelationship: subscription.billingRelationship || (isPaidOrTrial ? "paid_or_trial" : hasPaymentMethod ? "free_with_card" : "free_without_card"),
    billingStateSource: subscription.billingStateSource || "stripe",
    billingStateStale: subscription.billingStateStale || false
  };
}
function toLocalCachedSubscription(subscription: Subscription): Subscription {
  return {
    ...normalizeSubscriptionState(subscription),
    billingStateSource: "local_cache",
    billingStateStale: true
  };
}
function readCachedSubscription(uid: string): Subscription | null {
  if (typeof window === 'undefined') return null;
  try {
    const cached = window.localStorage.getItem(getSubscriptionCacheKey(uid));
    if (!cached) return null;
    return normalizeSubscriptionState(JSON.parse(cached) as Subscription);
  } catch (error) {
    console.warn('Failed to read cached subscription state:', error);
    return null;
  }
}
function writeCachedSubscription(uid: string, subscription: Subscription) {
  if (typeof window === 'undefined') return;
  try {
    const normalizedSubscription = normalizeSubscriptionState(subscription);
    window.localStorage.setItem(getSubscriptionCacheKey(uid), JSON.stringify({
      ...normalizedSubscription,
      billingStateSource: normalizedSubscription.billingStateSource === "local_cache" ? "stripe" : normalizedSubscription.billingStateSource,
      billingStateStale: false
    }));
  } catch (error) {
    console.warn('Failed to cache subscription state:', error);
  }
}
function isBillingAuthReadinessError(error: unknown): boolean {
  const maybeError = error as {
    code?: unknown;
    message?: unknown;
  };
  const code = String(maybeError?.code || '').toLowerCase();
  const message = String(maybeError?.message || '').toLowerCase();
  return code.includes('unauthenticated') || message.includes('must be authenticated') || message.includes('unauthenticated');
}
export const AuthContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    forceUpdate,
    forceUpdateValue
  } = useForceUpdate();
  const pagesRouter = usePagesRouter();
  const skipBillingFetchRef = useRef(true);
  skipBillingFetchRef.current = shouldSkipBillingFetchForPath(pagesRouter.asPath);
  const [user, setUser] = React.useState<FirebaseUser | null | undefined>(undefined);
  const [permissions, setPermissions] = React.useState<UserPermissions | null | undefined>(undefined);
  const [loading, setLoading] = React.useState(true);
  const [authDegraded, setAuthDegraded] = React.useState(false);
  const [billingAccountId, setBillingAccountId] = React.useState<string | null>(null);
  const authResolvedRef = useRef(false);
  // Tracks the verified bit already published to React. Auth does not emit
  // onAuthStateChanged when Admin SDK sets emailVerified on the same uid.
  const publishedEmailVerifiedRef = useRef(false);
  const [billingCredits, setBillingsCredits] = React.useState<number | undefined | null>(undefined);
  const [creditsLoading, setCreditsLoading] = React.useState(true);
  const [subscription, setSubscription] = React.useState<Subscription | null>(null);
  const [workspaces, setWorkspaces] = React.useState<WorkspaceMembership[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = React.useState(false);
  const [activeWorkspaceId, setActiveWorkspaceIdState] = React.useState<string | null>(null);
  const [accountModel, setAccountModel] = React.useState<string | null>(null);
  const [homeWorkspaceId, setHomeWorkspaceId] = React.useState<string | null>(null);
  const [authBootstrapComplete, setAuthBootstrapComplete] = React.useState(false);
  const activeWorkspaceIdRef = useRef<string | null>(null);
  const accountModelRef = useRef<string | null>(null);
  const homeWorkspaceIdRef = useRef<string | null>(null);
  activeWorkspaceIdRef.current = activeWorkspaceId;
  accountModelRef.current = accountModel;
  homeWorkspaceIdRef.current = homeWorkspaceId;
  const providerType = user?.providerData?.[0]?.providerId || "unknown";
  const SUBSCRIPTION_TTL_MS = 60000;
  const CREDITS_TTL_MS = 20000;
  const creditsRefreshInFlight = useRef(false);
  const creditsReconcileInFlight = useRef(false);
  const subscriptionRefreshInFlight = useRef(false);
  const creditsLastFetchedAt = useRef<number>(0);
  const subscriptionLastFetchedAt = useRef<number>(0);
  const creditsRequestId = useRef(0);
  const subscriptionRequestId = useRef(0);
  const creditsInFlightPromise = useRef<Promise<void> | null>(null);
  const subscriptionInFlightPromise = useRef<Promise<void> | null>(null);
  const lastGoodSubscriptionRef = useRef<Subscription | null>(null);

  // Track if credits have been successfully loaded at least once
  const creditsLoadedRef = useRef(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 5;
  const retryAuthInit = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      authResolvedRef.current = true;
      setAuthDegraded(false);
      if (user) {
        publishedEmailVerifiedRef.current = user.emailVerified === true;
        // Restore persisted workspace before billing effects run so the
        // first credits/subscription fetch targets the team context.
        const restored = readPersistedActiveWorkspaceId(user.uid);
        activeWorkspaceIdRef.current = restored;
        setActiveWorkspaceIdState(restored);
        setUser(user);
        Sentry.setUser({
          id: user.uid
        });
      } else {
        publishedEmailVerifiedRef.current = false;
        activeWorkspaceIdRef.current = null;
        accountModelRef.current = null;
        homeWorkspaceIdRef.current = null;
        setActiveWorkspaceIdState(null);
        setAccountModel(null);
        setHomeWorkspaceId(null);
        setUser(null);
        Sentry.setUser(null);
        clearAuthBootstrapProfileCache();
      }
      setLoading(false);
    });

    // Fail-open watchdog: never let a stalled auth init hang the whole UI.
    const watchdog = setTimeout(() => {
      if (authResolvedRef.current) return;
      // Resolve from the current user if available; otherwise treat as
      // signed out (route guards send to /login). We never assume auth.
      setUser(auth.currentUser ?? null);
      setLoading(false);
      setAuthDegraded(true);
    }, AUTH_INIT_WATCHDOG_MS);
    const unsubscribeToken = onIdTokenChanged(auth, nextUser => {
      if (!authResolvedRef.current) return;
      const verified = nextUser?.emailVerified === true;
      if (verified === publishedEmailVerifiedRef.current) return;
      publishedEmailVerifiedRef.current = verified;
      if (nextUser) {
        setUser(nextUser);
      }
      forceUpdate();
    });
    return () => {
      clearTimeout(watchdog);
      unsubscribe();
      unsubscribeToken();
    };
  }, [forceUpdate]);

  // Fallback bootstrap trigger for restored sessions/deep links.
  useEffect(() => {
    if (!user?.uid) {
      setAuthBootstrapComplete(false);
      return;
    }
    let cancelled = false;
    setAuthBootstrapComplete(false);
    accountModelRef.current = null;
    homeWorkspaceIdRef.current = null;
    setAccountModel(null);
    setHomeWorkspaceId(null);
    bootstrapAuthUser({
      uid: user.uid,
      providerType,
      source: "auth_context_bootstrap",
      loadProfile: false
    }).then(result => {
      if (cancelled) return;
      if (result.ensure?.billingAccountId) {
        setBillingAccountId(result.ensure.billingAccountId);
      }
      const nextAccountModel = result.ensure?.accountModel || null;
      const nextHomeWorkspaceId = result.ensure?.homeWorkspaceId || null;
      accountModelRef.current = nextAccountModel;
      homeWorkspaceIdRef.current = nextHomeWorkspaceId;
      setAccountModel(nextAccountModel);
      setHomeWorkspaceId(nextHomeWorkspaceId);
      const resolvedWorkspaceId = resolveActiveWorkspaceId({
        persistedWorkspaceId: readPersistedActiveWorkspaceId(user.uid),
        accountModel: nextAccountModel,
        homeWorkspaceId: nextHomeWorkspaceId
      });
      activeWorkspaceIdRef.current = resolvedWorkspaceId;
      setActiveWorkspaceIdState(resolvedWorkspaceId);
      if (resolvedWorkspaceId && resolvedWorkspaceId !== readPersistedActiveWorkspaceId(user.uid)) {
        writePersistedActiveWorkspaceId(user.uid, resolvedWorkspaceId);
      }
      setAuthBootstrapComplete(true);
    }).catch(error => {
      if (cancelled) return;
      console.warn("AuthContext bootstrap failed", error);
      // Fail open for billing/subscription refreshes. Auth bootstrap
      // failures already surface in the initiating flow; permanently
      // blocking billing here leaves authenticated users with empty state.
      setAuthBootstrapComplete(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, providerType]);

  // Reset the billing account id when the user changes/logs out.
  useEffect(() => {
    creditsRequestId.current += 1;
    subscriptionRequestId.current += 1;
    if (!user?.uid) {
      setBillingAccountId(null);
    }
  }, [user?.uid]);

  // Get the User's permissions on login or page load. Triggered when uid changes.
  useEffect(() => {
    if (user?.uid) {
      getUserPermissions().then(perm => {
        setPermissions(perm);
      }).catch(error => {
        // Resolve to null rather than leaving permissions undefined
        // forever — consumers treat undefined as "still loading".
        console.warn('Failed to load user permissions', error);
        setPermissions(null);
      });
    }
  }, [user?.uid, forceUpdateValue]);
  const isCurrentBillingContext = useCallback((workspaceIdAtFetch: string | null) => {
    return workspaceIdAtFetch === activeWorkspaceIdRef.current;
  }, []);

  // Switch the active context. `null` returns to the personal workspace.
  const setActiveWorkspaceId = useCallback((workspaceId: string | null) => {
    const nativeHomeId = accountModelRef.current === "workspace_native" ? homeWorkspaceIdRef.current : null;
    const nextWorkspaceId = workspaceId || (nativeHomeId ? nativeHomeId : null);
    activeWorkspaceIdRef.current = nextWorkspaceId;
    // Invalidate in-flight billing fetches so a slower personal response
    // cannot overwrite the newly-selected workspace context.
    creditsRequestId.current += 1;
    subscriptionRequestId.current += 1;
    lastGoodSubscriptionRef.current = null;
    setActiveWorkspaceIdState(nextWorkspaceId);
    if (user?.uid) {
      writePersistedActiveWorkspaceId(user.uid, nextWorkspaceId);
    }
  }, [user?.uid]);

  // Load (and refresh) the user's workspace memberships.
  const refreshWorkspaces = useCallback(async () => {
    if (!user?.uid) {
      setWorkspaces([]);
      return [];
    }
    setWorkspacesLoading(true);
    try {
      let memberships: WorkspaceMembership[] = [];
      let lastError: unknown;
      for (let attempt = 0; attempt < 3; attempt++) {
        const retryDelayMs = nextWorkspaceListRetryDelayMs(attempt);
        try {
          await user.getIdToken(attempt > 0);
          const {
            listMyWorkspaces
          } = await import("@/lib/Workspace");
          memberships = await listMyWorkspaces();
          lastError = undefined;
          break;
        } catch (error) {
          lastError = error;
          if (retryDelayMs == null || attempt === 2) {
            throw error;
          }
          await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        }
      }
      if (lastError) {
        throw lastError;
      }
      setWorkspaces(memberships);
      writeCachedWorkspaceMemberships(user.uid, memberships);

      // Only demote to Personal after a successful list that no longer
      // includes the persisted id. A failed/empty cold-start fetch must
      // not wipe team context.
      const current = activeWorkspaceIdRef.current;
      if (shouldClearStaleActiveWorkspace({
        fetchSucceeded: true,
        currentWorkspaceId: current,
        membershipIds: memberships.map(m => m.workspace.id)
      })) {
        setActiveWorkspaceId(null);
      }
      return memberships;
    } catch (error) {
      console.warn('Failed to load workspaces', error);
      const cached = readCachedWorkspaceMemberships(user.uid);
      if (cached.length > 0) {
        setWorkspaces(cached);
      }
      return cached;
    } finally {
      setWorkspacesLoading(false);
    }
  }, [user?.uid, setActiveWorkspaceId]);

  // Restore persisted context immediately, paint cached memberships, then
  // refresh after auth bootstrap so the callable has a usable token.
  useEffect(() => {
    if (!user?.uid) {
      setWorkspaces([]);
      setActiveWorkspaceIdState(null);
      return;
    }
    const persisted = readPersistedActiveWorkspaceId(user.uid);
    const resolved = resolveActiveWorkspaceId({
      persistedWorkspaceId: persisted,
      accountModel: accountModelRef.current,
      homeWorkspaceId: homeWorkspaceIdRef.current
    });
    activeWorkspaceIdRef.current = resolved;
    setActiveWorkspaceIdState(resolved);
    if (resolved && resolved !== persisted) {
      writePersistedActiveWorkspaceId(user.uid, resolved);
    }
    const cached = readCachedWorkspaceMemberships(user.uid);
    if (cached.length > 0) {
      setWorkspaces(cached);
    }
    if (!authBootstrapComplete) {
      setWorkspacesLoading(true);
      return;
    }
    refreshWorkspaces();
  }, [user?.uid, authBootstrapComplete, refreshWorkspaces]);
  const getTotalCreditsForAuthenticatedUser = useCallback(async () => {
    if (!user?.uid) {
      throw new Error('Cannot fetch billing credits before auth state is ready');
    }

    // Ensure Functions receives auth context; onAuthStateChanged can fire
    // before the callable request has a usable token attached.
    await user.getIdToken();
    const {
      getTotalCredits
    } = await import("@/lib/Billing");
    return getTotalCredits(activeWorkspaceIdRef.current);
  }, [user]);

  // Fetch billing credits with retry logic
  const fetchBillingCredits = useCallback(async (isRetry = false): Promise<boolean> => {
    if (skipBillingFetchRef.current) return false;
    if (!user?.uid) return false;
    const workspaceIdAtFetch = activeWorkspaceIdRef.current;
    const requestId = creditsRequestId.current;
    try {
      const {
        credits
      } = await getTotalCreditsForAuthenticatedUser();
      if (requestId !== creditsRequestId.current || !isCurrentBillingContext(workspaceIdAtFetch)) {
        return false;
      }
      setBillingsCredits(credits);
      if (user?.uid) writeCachedCredits(contextCacheId(user.uid, workspaceIdAtFetch), credits);
      creditsLoadedRef.current = true;
      creditsLastFetchedAt.current = Date.now();
      retryCountRef.current = 0;
      setCreditsLoading(false);
      return true;
    } catch (error) {
      if (isBillingAuthReadinessError(error)) {
        if (!isRetry) {
          console.warn('Billing credits not ready for authenticated user yet:', error);
        }
      } else {
        console.error(`${isRetry ? 'Retry: ' : ''}Failed to fetch billing credits:`, error);
      }
      return false;
    }
  }, [getTotalCreditsForAuthenticatedUser, isCurrentBillingContext, user?.uid]);

  // Get and set billingCredits using useEffect
  useEffect(() => {
    if (!user?.uid) {
      // Reset only when the user logs out. Do not clear cached credits
      // while auth bootstrap is still in progress.
      setBillingsCredits(undefined);
      creditsLoadedRef.current = false;
      retryCountRef.current = 0;
      setCreditsLoading(false);
      return;
    }
    if (skipBillingFetchRef.current) {
      return;
    }
    if (!authBootstrapComplete) {
      const cachedCredits = readCachedCredits(contextCacheId(user.uid, activeWorkspaceId));
      if (cachedCredits !== null) {
        setBillingsCredits(cachedCredits);
      }
      setCreditsLoading(true);
      return;
    }
    if (user.uid && authBootstrapComplete) {
      let isMounted = true;
      setCreditsLoading(true);
      creditsLoadedRef.current = false;

      // Instant paint from the last-known balance while the authoritative
      // fetch is in flight (stale-while-revalidate, mirrors subscription).
      const cachedCredits = readCachedCredits(contextCacheId(user.uid, activeWorkspaceId));
      if (cachedCredits !== null) {
        setBillingsCredits(cachedCredits);
      }

      // Initial fetch
      fetchBillingCredits().then(success => {
        if (!success && isMounted) {
          // Schedule retries with exponential backoff
          const scheduleRetry = (delay: number, retryNum: number) => {
            if (retryNum > MAX_RETRIES) {
              setCreditsLoading(false);
              return;
            }
            setTimeout(async () => {
              if (!isMounted || creditsLoadedRef.current) return;
              const success = await fetchBillingCredits(true);
              if (!success && isMounted && !creditsLoadedRef.current) {
                // Schedule next retry with increasing delay
                scheduleRetry(delay * 1.5, retryNum + 1);
              }
            }, delay);
          };

          // Start retry sequence after 2 seconds
          scheduleRetry(2000, 1);
        }
      });
      return () => {
        isMounted = false;
      };
    }
  }, [user?.uid, authBootstrapComplete, activeWorkspaceId, forceUpdateValue, fetchBillingCredits, pagesRouter.asPath]);
  useEffect(() => {
    if (!user?.uid) {
      lastGoodSubscriptionRef.current = null;
      setSubscription(null);
      return;
    }
    const cachedSubscription = readCachedSubscription(contextCacheId(user.uid, activeWorkspaceId));
    if (cachedSubscription) {
      const staleCachedSubscription = toLocalCachedSubscription(cachedSubscription);
      lastGoodSubscriptionRef.current = cachedSubscription;
      setSubscription(staleCachedSubscription);
    } else {
      lastGoodSubscriptionRef.current = null;
      setSubscription(null);
    }
  }, [user?.uid, activeWorkspaceId]);
  const refreshBillingCredits = useCallback(async (options?: {
    silent?: boolean;
    force?: boolean;
  }) => {
    if (skipBillingFetchRef.current) return;
    if (!user?.uid) return;
    if (!options?.force && creditsInFlightPromise.current) {
      await creditsInFlightPromise.current;
      return;
    }
    if (!options?.force && creditsRefreshInFlight.current) return;
    creditsRefreshInFlight.current = true;
    if (!options?.silent) {
      setCreditsLoading(true);
    }
    const requestId = ++creditsRequestId.current;
    const workspaceIdAtFetch = activeWorkspaceIdRef.current;
    try {
      const inFlight = getTotalCreditsForAuthenticatedUser().then(({
        credits
      }) => {
        if (requestId !== creditsRequestId.current || !isCurrentBillingContext(workspaceIdAtFetch)) return;
        setBillingsCredits(credits);
        if (user?.uid) writeCachedCredits(contextCacheId(user.uid, workspaceIdAtFetch), credits);
        creditsLoadedRef.current = true;
        creditsLastFetchedAt.current = Date.now();
      });
      creditsInFlightPromise.current = inFlight;
      await inFlight;
    } catch (error) {
      if (isBillingAuthReadinessError(error)) {
        if (!options?.silent) {
          console.warn('Billing credits refresh skipped until auth is ready:', error);
        }
      } else {
        console.error('Failed to refresh billing credits:', error);
      }
      // Try one more time after a short delay
      setTimeout(async () => {
        try {
          const {
            credits
          } = await getTotalCreditsForAuthenticatedUser();
          if (requestId !== creditsRequestId.current || !isCurrentBillingContext(workspaceIdAtFetch)) return;
          setBillingsCredits(credits);
          if (user?.uid) writeCachedCredits(contextCacheId(user.uid, workspaceIdAtFetch), credits);
          creditsLoadedRef.current = true;
          creditsLastFetchedAt.current = Date.now();
        } catch (retryError) {
          if (isBillingAuthReadinessError(retryError)) {
            if (!options?.silent) {
              console.warn('Billing credits refresh retry skipped until auth is ready:', retryError);
            }
          } else {
            console.error('Refresh retry failed:', retryError);
          }
        }
      }, 1000);
    } finally {
      creditsRefreshInFlight.current = false;
      creditsInFlightPromise.current = null;
      if (!options?.silent) {
        setCreditsLoading(false);
      }
    }
  }, [getTotalCreditsForAuthenticatedUser, isCurrentBillingContext, user?.uid]);
  const activeWorkspace = activeWorkspaceId ? workspaces.find(m => m.workspace.id === activeWorkspaceId) || null : null;
  const effectiveBillingAccountId = activeWorkspaceId ? activeWorkspace?.workspace.billingAccountId ?? null : billingAccountId;

  // Realtime credit invalidation (feature-flagged). Uses the snapshot purely as
  // a "something changed" trigger and re-fetches via the authoritative callable,
  // so plan-allowance/overage logic stays server-owned. No-ops (with a single
  // warning) if rules deny the read.
  useEffect(() => {
    if (!REALTIME_CREDITS_ENABLED) return;
    if (!user?.uid || !effectiveBillingAccountId) return;
    let isFirstSnapshot = true;
    const billingAccountRef = doc(firestore, 'BillingAccounts', effectiveBillingAccountId);
    const unsubscribe = onSnapshot(billingAccountRef, () => {
      if (isFirstSnapshot) {
        isFirstSnapshot = false;
        return;
      }
      refreshBillingCredits({
        silent: true
      });
    }, error => {
      console.warn('Realtime credits listener disabled:', error);
    });
    return () => unsubscribe();
  }, [user?.uid, effectiveBillingAccountId, refreshBillingCredits]);

  // Apply a server-authoritative balance returned by a credit-consuming
  // callable: update state, stamp the fetch clock (so TTL logic treats it as
  // fresh and won't immediately refetch), cache for instant paint, and notify
  // other tabs/windows to reconcile.
  const applyAuthoritativeCredits = useCallback((balance: number) => {
    if (!Number.isFinite(balance)) return;
    // Invalidate any in-flight getTotalCredits response so a slower pre-
    // purchase read cannot stomp this authoritative balance (and the nav
    // credit counters) after the purchase succeeds.
    creditsRequestId.current += 1;
    setBillingsCredits(balance);
    creditsLoadedRef.current = true;
    creditsLastFetchedAt.current = Date.now();
    if (user?.uid) writeCachedCredits(contextCacheId(user.uid, activeWorkspaceIdRef.current), balance);
    broadcastBillingUpdated();
  }, [user?.uid]);

  // Provisionally debit a spend the moment an action starts, so the nav
  // counters can't be read as "still affordable" while the callable runs.
  // Functional update because callers routinely hold a stale `billingCredits`
  // closure — most notably an action replayed right after a credit top-up.
  // Deliberately no cache write or cross-tab broadcast: this number is a
  // guess, and the callable's returned balance (or a refresh on failure)
  // settles the real one.
  const spendCreditsOptimistic = useCallback((cost: number) => {
    if (!Number.isFinite(cost) || cost <= 0) return;
    creditsRequestId.current += 1;
    setBillingsCredits(current => typeof current === "number" && Number.isFinite(current) ? Math.max(0, current - cost) : current);
  }, []);

  // Poll the authoritative balance after a purchase whose fulfilment happens
  // server-side (Stripe Checkout -> webhook mint), so every consumer of
  // `billingCredits` — including the nav credit counters — lands on the new
  // balance as soon as it exists instead of waiting for a reload.
  const reconcileCreditsAfterPurchase = useCallback(async (options?: {
    baseline?: number | null;
    timeoutMs?: number;
  }) => {
    if (!user?.uid) return;
    if (creditsReconcileInFlight.current) return;
    creditsReconcileInFlight.current = true;
    const baseline = options?.baseline ?? null;
    const timeoutMs = options?.timeoutMs ?? CREDIT_RECONCILE_TIMEOUT_MS;
    const startedAt = Date.now();
    const workspaceIdAtFetch = activeWorkspaceIdRef.current;
    try {
      for (;;) {
        let observed: number | null = null;
        const requestId = ++creditsRequestId.current;
        try {
          const {
            credits
          } = await getTotalCreditsForAuthenticatedUser();
          if (requestId !== creditsRequestId.current || !isCurrentBillingContext(workspaceIdAtFetch)) {
            return;
          }
          observed = credits;
          setBillingsCredits(credits);
          writeCachedCredits(contextCacheId(user.uid, workspaceIdAtFetch), credits);
          creditsLoadedRef.current = true;
          creditsLastFetchedAt.current = Date.now();
          setCreditsLoading(false);
        } catch (error) {
          if (!isBillingAuthReadinessError(error)) {
            console.warn('Credit reconciliation read failed:', error);
          }
        }
        const step = nextCreditReconcileStep({
          baseline,
          observed,
          elapsedMs: Date.now() - startedAt,
          timeoutMs
        });
        if (step === "settled") {
          broadcastBillingUpdated();
          return;
        }
        if (step === "timed-out") return;
        await new Promise(resolve => setTimeout(resolve, CREDIT_RECONCILE_INTERVAL_MS));
        if (!isCurrentBillingContext(workspaceIdAtFetch)) return;
      }
    } finally {
      creditsReconcileInFlight.current = false;
    }
  }, [getTotalCreditsForAuthenticatedUser, isCurrentBillingContext, user?.uid]);
  const fetchSubscription = useCallback(async (): Promise<void> => {
    if (skipBillingFetchRef.current) return;
    if (!user?.uid) return;
    const subscriptionUid = resolveSelfSubscriptionRetrieveUid(user.uid, auth.currentUser?.uid);
    if (!subscriptionUid) return;
    const workspaceIdAtFetch = activeWorkspaceIdRef.current;
    const requestId = subscriptionRequestId.current;
    try {
      await user.getIdToken();
      if (requestId !== subscriptionRequestId.current || resolveSelfSubscriptionRetrieveUid(subscriptionUid, auth.currentUser?.uid) !== subscriptionUid) {
        return;
      }
      const {
        retrieveSubscription
      } = await import("@/lib/Subscription");
      const nextSubscription = normalizeSubscriptionState(await retrieveSubscription(subscriptionUid, workspaceIdAtFetch));
      if (requestId !== subscriptionRequestId.current || !isCurrentBillingContext(workspaceIdAtFetch)) {
        return;
      }
      subscriptionLastFetchedAt.current = Date.now();
      if (nextSubscription.billingStateStale && lastGoodSubscriptionRef.current) {
        setSubscription(toLocalCachedSubscription(lastGoodSubscriptionRef.current));
        return;
      }
      setSubscription(nextSubscription);
      if (!nextSubscription.billingStateStale) {
        lastGoodSubscriptionRef.current = nextSubscription;
        writeCachedSubscription(contextCacheId(user.uid, workspaceIdAtFetch), nextSubscription);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      if (requestId !== subscriptionRequestId.current || !isCurrentBillingContext(workspaceIdAtFetch)) {
        return;
      }
      if (lastGoodSubscriptionRef.current) {
        setSubscription(toLocalCachedSubscription(lastGoodSubscriptionRef.current));
      }
    }
  }, [isCurrentBillingContext, user?.uid]);
  const refreshSubscription = useCallback(async (options?: {
    silent?: boolean;
    force?: boolean;
  }) => {
    if (!options?.force && subscriptionInFlightPromise.current) {
      await subscriptionInFlightPromise.current;
      return;
    }
    if (!options?.force && subscriptionRefreshInFlight.current) return;
    subscriptionRefreshInFlight.current = true;
    const requestId = ++subscriptionRequestId.current;
    const inFlight = fetchSubscription().finally(() => {
      if (requestId === subscriptionRequestId.current) {
        subscriptionRefreshInFlight.current = false;
      }
    });
    subscriptionInFlightPromise.current = inFlight;
    await inFlight;
    if (requestId === subscriptionRequestId.current) {
      subscriptionInFlightPromise.current = null;
    }
  }, [fetchSubscription]);
  const refreshBillingState = useCallback(async (options?: {
    force?: boolean;
    silent?: boolean;
  }) => {
    const now = Date.now();
    const shouldRefreshSubscription = options?.force || now - subscriptionLastFetchedAt.current > SUBSCRIPTION_TTL_MS;
    const shouldRefreshCredits = options?.force || now - creditsLastFetchedAt.current > CREDITS_TTL_MS;
    const tasks: Promise<void>[] = [];
    if (shouldRefreshSubscription) {
      tasks.push(refreshSubscription({
        silent: options?.silent,
        force: options?.force
      }));
    }
    if (shouldRefreshCredits) {
      tasks.push(refreshBillingCredits({
        silent: options?.silent,
        force: options?.force
      }));
    }
    if (tasks.length === 0) return;
    await Promise.all(tasks);
  }, [refreshBillingCredits, refreshSubscription]);

  // Get the subscription details
  useEffect(() => {
    if (skipBillingFetchRef.current) return;
    if (user?.uid && authBootstrapComplete) {
      fetchSubscription();
    }
  }, [user?.uid, authBootstrapComplete, activeWorkspaceId, forceUpdateValue, fetchSubscription, pagesRouter.asPath]);

  // Refresh subscription/credits when billing is updated in another tab
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === USAGE_BILLING_UPDATED_KEY) {
        refreshBillingState({
          silent: true,
          force: true
        });
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [refreshSubscription, refreshBillingCredits]);

  // Refresh on BroadcastChannel events
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(BILLING_CHANNEL_NAME);
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'billing-updated') {
        refreshBillingState({
          silent: true,
          force: true
        });
      }
    };
    channel.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, [refreshSubscription, refreshBillingCredits]);
  return <AuthContext.Provider value={{
    user,
    permissions,
    billingCredits,
    setBillingsCredits,
    applyAuthoritativeCredits,
    spendCreditsOptimistic,
    reconcileCreditsAfterPurchase,
    refreshBillingCredits,
    refreshSubscription,
    refreshBillingState,
    loading,
    forceUpdate,
    subscription,
    creditsLoading,
    authDegraded,
    retryAuthInit,
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    setActiveWorkspaceId,
    refreshWorkspaces,
    workspacesLoading,
    accountModel,
    homeWorkspaceId
  }} data-sentry-element="unknown" data-sentry-component="AuthContextProvider" data-sentry-source-file="AuthContext.tsx">
            {children}
        </AuthContext.Provider>;
};

/**
 * Hook to get the current user
 * @param forceAuthRedirect - Force redirect to login page if user is not authenticated
 */
export function useUser(forceAuthRedirect = false) {
  const {
    user,
    loading,
    authDegraded
  } = useAuthContext();
  const router = useRouter();
  React.useEffect(() => {
    if (!forceAuthRedirect || loading || authDegraded || user !== null) {
      return;
    }
    // Firebase already restored a session; wait for AuthContext to catch up
    // instead of treating the watchdog/null flicker as signed-out.
    if (currentUser()) {
      return;
    }
    // Preserve the current path so post-login can return to deep links
    // like /workspace/accept?invite=...
    const asPath = typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/";
    const safeReturnTo = getSafeReturnTo(asPath);
    router.push(safeReturnTo ? getAuthHref("/login", safeReturnTo) : "/login");
  }, [user, loading, authDegraded, forceAuthRedirect, router]);
  return user;
}

/**
 * Hook to get the permissions for the current user
 */
export function usePermissions() {
  const {
    permissions
  } = useAuthContext();
  return permissions;
}

/**
 * Hook to enforce a permission with a redirect if the specified attribute is false
 * @param attribute
 * @param destination
 */
export function usePermissionsWithRedirect(attribute: string, destination: string) {
  const router = useRouter();
  const {
    permissions
  } = useAuthContext();
  React.useEffect(() => {
    // @ts-ignore
    if (permissions && !permissions?.[attribute]) {
      router.push(destination);
    }
  }, [permissions]);
  return permissions;
}

/**
 * Hook to get the billingCredits for the current user
 */
export function useBillingCredits() {
  const {
    billingCredits,
    setBillingsCredits,
    applyAuthoritativeCredits,
    spendCreditsOptimistic,
    reconcileCreditsAfterPurchase,
    refreshBillingCredits,
    creditsLoading
  } = useAuthContext();
  return {
    billingCredits,
    setBillingsCredits,
    applyAuthoritativeCredits,
    spendCreditsOptimistic,
    reconcileCreditsAfterPurchase,
    refreshBillingCredits,
    creditsLoading
  };
}

/**
 * Hook to enforce admin permission with a redirect if the user is not an admin
 */
export function useAdminPermissions() {
  const {
    user,
    permissions
  } = useAuthContext();
  const router = useRouter();
  useEffect(() => {
    if (user === null) {
      router.push('/login');
    }
    if (permissions !== undefined && !permissions?.admin) {
      router.push('/video');
    }
  }, [user?.uid, permissions?.admin]);
}

/**
 * Non-hook function to get the current user and permissions
 * This should not be the primary way to get user and permissions as it is less efficient than using hooks
 * Only to be used when requiring this information outside the AuthContext
 * @return {Promise<{user: FirebaseUser | null | undefined, permissions: UserPermissions | null | undefined, isAdmin: boolean | undefined}>}
 */
export async function getUserAndPermissions() {
  const user = currentUser();
  const uid = user?.uid || '';
  const permissions = await getUserPermissions();
  const isAdmin = permissions?.admin;
  return {
    user,
    uid,
    permissions,
    isAdmin
  };
}

/**
 * Hook to get the current subscription
 */
export function useSubscription() {
  const {
    subscription,
    refreshSubscription
  } = useAuthContext();
  return {
    subscription,
    refreshSubscription
  };
}
export function useBillingState() {
  const {
    subscription,
    billingCredits,
    refreshBillingState
  } = useAuthContext();
  return {
    subscription,
    billingCredits,
    refreshBillingState
  };
}

/**
 * Hook for team-workspace state. `activeWorkspaceId === null` is the personal
 * context. `isTeamContext` is a convenience boolean for gating UI/behavior.
 */
export function useWorkspaces() {
  const {
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    setActiveWorkspaceId,
    refreshWorkspaces,
    workspacesLoading,
    accountModel,
    homeWorkspaceId
  } = useAuthContext();
  const role = activeWorkspace?.role;
  const isTeamContext = activeWorkspaceId !== null;
  const hideWorkspaceChrome = shouldHideNativeHomeChrome({
    accountModel,
    homeWorkspaceId,
    membershipWorkspaceIds: workspaces.map(membership => membership.workspace.id)
  });
  const offerPersonalContext = shouldOfferPersonalContext(accountModel);
  // Personal context: treat as producer for own library. Team: owner/contributor only.
  // While memberships are still loading, do not treat a missing role as a
  // member-cannot-produce denial — that is the cold-load footgun.
  const canProduce = canProduceInActiveContext({
    isTeamContext,
    role,
    workspacesLoading,
    hasResolvedMembership: Boolean(activeWorkspace)
  });
  const canManage = isTeamContext && role === "owner";
  return {
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    setActiveWorkspaceId,
    refreshWorkspaces,
    workspacesLoading,
    isTeamContext,
    canProduce,
    canManage,
    hideWorkspaceChrome,
    offerPersonalContext
  };
}